import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type { AdminHostAgent } from '../../../types/admin';
import type {
  CustomLaunchParameterDefinition,
  CustomParameterValue,
  CustomProduct,
  CustomProductRevision,
} from '../../../types/custom';
import { customErrorMessage, formatBytes, newIdempotencyKey } from '../../../utils/customUi';
import { CustomInstancesIndex } from './CustomInstancesIndex';

export function CustomInstancesPanel({
  product,
  revisions,
  agents,
}: {
  product: CustomProduct;
  revisions: CustomProductRevision[];
  agents: AdminHostAgent[];
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [imageId, setImageId] = useState('');
  const [replicaId, setReplicaId] = useState('');
  const [resourceProfile, setResourceProfile] = useState('');
  const [values, setValues] = useState<Record<string, CustomParameterValue>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const imagesQuery = useQuery({
    queryKey: ['custom-products', product.id, 'images'],
    queryFn: async () => (await customAdminApi.listImages(product.id)).images,
    refetchInterval: 5_000,
  });

  const usableImages = useMemo(
    () => (imagesQuery.data || []).filter(image => (
      ['ready', 'partially_ready'].includes(image.status)
      && image.replicas.some(replica => replica.status === 'ready')
    )),
    [imagesQuery.data],
  );
  const selectedImage = usableImages.find(image => image.id === imageId) || usableImages[0];
  const readyReplicas = selectedImage?.replicas.filter(replica => replica.status === 'ready') || [];
  const selectedReplica = readyReplicas.find(replica => replica.replicaId === replicaId) || readyReplicas[0];
  const selectedRevision = revisions.find(revision => revision.revision === selectedImage?.productRevision);
  const definition = selectedRevision?.definition;
  const definitionRef = useRef(definition);
  definitionRef.current = definition;

  useEffect(() => {
    if (!selectedImage) {
      setImageId('');
      setReplicaId('');
      return;
    }
    if (imageId !== selectedImage.id) setImageId(selectedImage.id);
    const nextReplica = selectedImage.replicas.find(replica => replica.replicaId === replicaId && replica.status === 'ready')
      || selectedImage.replicas.find(replica => replica.status === 'ready');
    if (nextReplica && nextReplica.replicaId !== replicaId) setReplicaId(nextReplica.replicaId);
  }, [imageId, replicaId, selectedImage]);

  useEffect(() => {
    const currentDefinition = definitionRef.current;
    if (!currentDefinition) {
      setValues({});
      setResourceProfile('');
      return;
    }
    const defaults: Record<string, CustomParameterValue> = {};
    for (const parameter of currentDefinition.launch.parameters) {
      if (parameter.default !== undefined) defaults[parameter.key] = parameter.default;
      else if (parameter.type === 'boolean') defaults[parameter.key] = false;
    }
    setValues(defaults);
    setResourceProfile(currentDefinition.resourcePolicy.defaultProfile);
  }, [selectedRevision?.id]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedImage || !selectedReplica || !definition) {
      setError('Choose an image with a ready Agent replica first.');
      return;
    }
    const parameters: Record<string, CustomParameterValue> = {};
    for (const parameter of definition.launch.parameters) {
      const value = values[parameter.key];
      if (value === undefined || (value === '' && !parameter.required)) continue;
      parameters[parameter.key] = value;
    }
    setBusy('create');
    setError('');
    try {
      const result = await customAdminApi.createInstance({
        productId: product.id,
        imageId: selectedImage.id,
        imageReplicaId: selectedReplica.replicaId,
        agentId: selectedReplica.agentId,
        name,
        parameters,
        ...(resourceProfile ? { resourceProfile } : {}),
      }, newIdempotencyKey('instance-create'));
      setNotice(`Instance ${name} accepted as operation ${result.operationId} and pinned to ${selectedImage.version} on ${agentName(selectedReplica.agentId, agents)}.`);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['custom-instances'] });
    } catch (caught) {
      setError(customErrorMessage(caught, 'The custom instance could not be created.'));
    } finally {
      setBusy(null);
    }
  };

  return <div className="space-y-5">
    <form className="card p-5" onSubmit={create}>
      <div className="flex flex-wrap justify-between gap-3"><div><h3 className="font-semibold text-gray-200 flex items-center gap-2"><Plus className="w-4 h-4 text-neon-400" /> Create schema-driven instance</h3><p className="text-xs text-gray-500 mt-1">Image revision + ready replica + typed parameters resolve once in Mothership.</p></div>{product.status !== 'active' && <span className="badge-warning">Activate the product before creating instances</span>}</div>
      {usableImages.length === 0 ? <div className="mt-4 p-4 rounded border border-amber-500/20 bg-amber-500/10 text-sm text-amber-300">Image not ready: upload and build at least one replica successfully before creating an instance.</div> : <div className="space-y-4 mt-4">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="text-xs text-gray-500">Image release<select className="input mt-1" value={selectedImage?.id || ''} onChange={event => { setImageId(event.target.value); setReplicaId(''); }}>{usableImages.map(image => <option key={image.id} value={image.id}>{image.version} · revision {image.productRevision}</option>)}</select></label>
          <label className="text-xs text-gray-500">Ready Agent replica<select className="input mt-1" value={selectedReplica?.replicaId || ''} onChange={event => setReplicaId(event.target.value)}>{readyReplicas.map(replica => <option key={replica.replicaId} value={replica.replicaId}>{agentName(replica.agentId, agents)} · {replica.dockerImageId?.slice(0, 18)}</option>)}</select></label>
          <label className="text-xs text-gray-500">Resource profile<select className="input mt-1" value={resourceProfile} onChange={event => setResourceProfile(event.target.value)}>{definition?.resourcePolicy.profiles.map(profile => <option key={profile.key} value={profile.key}>{profile.label} · {profile.cpuCores} CPU · {formatBytes(profile.memoryBytes)} RAM · {formatBytes(profile.storageBytes)}</option>)}</select></label>
        </div>
        <label className="block text-xs text-gray-500">Instance name<input className="input mt-1" required minLength={1} maxLength={128} value={name} placeholder="My custom server" onChange={event => setName(event.target.value)} /></label>
        {definition && <div className="grid md:grid-cols-2 gap-3">{definition.launch.parameters.map(parameter => <ParameterField key={parameter.key} parameter={parameter} value={values[parameter.key]} onChange={value => setValues(current => ({ ...current, [parameter.key]: value }))} />)}</div>}
        {definition && <div className="text-xs text-gray-500 flex flex-wrap gap-x-5 gap-y-1"><span>{definition.launch.ports.length} declared port(s)</span><span>{definition.launch.volumes.length} persistent mount(s)</span><span>working dir {definition.launch.workingDirectory}</span><span>stop timeout {definition.launch.stopTimeoutSeconds}s</span></div>}
        <div className="flex justify-end"><button className="btn-primary" disabled={busy === 'create' || product.status !== 'active'}><Plus className="w-4 h-4" /> {busy === 'create' ? 'Creating…' : 'Create instance'}</button></div>
      </div>}
    </form>

    {error && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300">{error}</div>}
    {notice && <div className="p-3 rounded border border-neon-500/20 bg-neon-500/10 text-sm text-neon-300">{notice}</div>}
    <CustomInstancesIndex fixedProductId={product.id} embedded />
  </div>;
}

function ParameterField({
  parameter,
  value,
  onChange,
}: {
  parameter: CustomLaunchParameterDefinition;
  value: CustomParameterValue | undefined;
  onChange: (value: CustomParameterValue) => void;
}) {
  const help = [
    parameter.description,
    parameter.min !== undefined || parameter.max !== undefined ? `${parameter.min ?? '−∞'}…${parameter.max ?? '∞'}` : '',
    parameter.pattern ? `pattern ${parameter.pattern}` : '',
  ].filter(Boolean).join(' · ');
  if (parameter.type === 'boolean') {
    return <label className="rounded-lg border border-dark-600 p-3 flex items-start gap-3 text-sm text-gray-300"><input className="checkbox mt-0.5" type="checkbox" checked={value === true} onChange={event => onChange(event.target.checked)} /><span><span>{parameter.label}{parameter.required ? ' *' : ''}</span>{help && <span className="block text-xs text-gray-500 mt-1">{help}</span>}</span></label>;
  }
  return <label className="text-xs text-gray-500">{parameter.label}{parameter.required ? ' *' : ''}{parameter.type === 'enum' ? <select className="input mt-1" required={parameter.required} value={String(value ?? '')} onChange={event => onChange(event.target.value)}><option value="" disabled={parameter.required}>Select…</option>{parameter.options?.map(option => <option key={option} value={option}>{option}</option>)}</select> : <input className="input mt-1" type={parameter.type === 'integer' ? 'number' : 'text'} required={parameter.required} min={parameter.min} max={parameter.max} minLength={parameter.minLength} maxLength={parameter.maxLength} pattern={parameter.pattern} value={value === undefined ? '' : String(value)} onChange={event => onChange(parameter.type === 'integer' && event.target.value !== '' ? Number(event.target.value) : event.target.value)} />}{help && <span className="block mt-1 text-gray-600">{help}</span>}</label>;
}

function agentName(agentId: string, agents: AdminHostAgent[]) {
  return agents.find(agent => agent._id === agentId)?.name || agentId;
}
